import React, { useState, useEffect } from 'react';
import { AIWaveResponse } from './types';
import { Sparkles, Loader2, AlertCircle, LogIn } from 'lucide-react';
import { generateWavePattern, isGeminiConfigured } from '../../services/geminiService';
import authService from '../../services/authService';

interface AIPanelProps {
  onAIResponse: (response: AIWaveResponse) => void;
  onSignInClick?: () => void;
}

const AIPanel: React.FC<AIPanelProps> = ({ onAIResponse, onSignInClick }) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configured, setConfigured] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [singleBurst, setSingleBurst] = useState(true); // emit once to form shapes

  useEffect(() => {
    setConfigured(isGeminiConfigured());
    // Check authentication status
    setIsAuthenticated(authService.isAuthenticated());
    // Subscribe to auth changes
    const unsubscribe = authService.onAuthStateChange((user) => {
      setIsAuthenticated(user !== null);
    });
    return unsubscribe;
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    if (!isAuthenticated) {
      setError('Please sign in to use AI features');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await generateWavePattern(prompt, { loop: !singleBurst });
      onAIResponse({
        ...response,
        config: {
          ...response.config,
          loop: !singleBurst,
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate pattern';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (!configured) {
    return (
      <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-md">
        <div className="flex items-center gap-2 mb-3 text-yellow-600">
          <AlertCircle size={16} />
          <h3 className="font-bold text-xs tracking-wider uppercase">AI Configuration</h3>
        </div>
        <div className="text-[11px] text-gray-600 space-y-2">
          <p>
            To enable AI wave generation, set your Google AI API key:
          </p>
          <div className="bg-gray-50 border border-gray-300 rounded p-2 font-mono text-[10px] text-blue-700 overflow-auto">
            VITE_GOOGLE_AI_KEY=your_api_key_here
          </div>
          <p className="text-gray-500 text-[10px]">
            Get a free key at{' '}
            <a
              href="https://aistudio.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              aistudio.google.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-md">
        <div className="flex items-center gap-2 mb-3 text-orange-600">
          <LogIn size={16} />
          <h3 className="font-bold text-xs tracking-wider uppercase">Sign In Required</h3>
        </div>
        <div className="text-[11px] text-gray-600 space-y-3">
          <p>
            AI features are only available to signed-in users. Please sign in to access the AI Wave Designer.
          </p>
          <button
            onClick={onSignInClick}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 rounded transition-all flex items-center justify-center gap-2 text-xs shadow-md"
          >
            <LogIn size={12} />
            Sign In Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-md">
      <div className="flex items-center gap-2 mb-3 text-blue-600">
        <Sparkles size={16} />
        <h3 className="font-bold text-xs tracking-wider uppercase">AI Wave Designer</h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe a wave pattern... e.g., 'Two boats passing in opposite directions' or 'Calm breathing from center'"
            className="w-full bg-gray-50 border border-gray-300 rounded p-2 text-xs text-gray-800 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-none h-20"
          />
        </div>

        {error && (
          <div className="flex items-start gap-2 text-red-700 text-[11px] bg-red-50 p-2 rounded border border-red-300">
            <AlertCircle size={11} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <label className="flex items-center gap-2 text-[11px] text-gray-700 select-none">
          <input
            type="checkbox"
            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            checked={singleBurst}
            onChange={(e) => setSingleBurst(e.target.checked)}
          />
          Single burst (no looping) — best for printing shapes/words via interference
        </label>

        <button
          type="submit"
          disabled={loading || !prompt.trim()}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-xs shadow-md"
        >
          {loading ? (
            <>
              <Loader2 size={12} className="animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles size={12} />
              Generate Pattern
            </>
          )}
        </button>
      </form>

      <div className="mt-3 pt-3 border-t border-gray-200">
        <p className="text-[10px] text-gray-500 leading-relaxed">
          Powered by Gemini 2.0. Describe any wave phenomenon and AI will calculate optimal emitter positions and frequencies.
        </p>
      </div>
    </div>
  );
};

export default AIPanel;
